import { useEffect, useState } from 'react';
import { flushSync } from 'react-dom';

/** Miroir des jetons CSS --duree-* (theme.css) ; un test vérifie la parité. */
export const DUREES = { instant: 100, rapide: 150, base: 200, feuille: 500 /* vaul, fixe */ } as const;

/** Vrai si l'utilisateur demande moins d'animations, ou si le navigateur ne sait pas le dire. */
export function mouvementReduit(): boolean {
  return (
    typeof window.matchMedia !== 'function' || window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

/**
 * Garde la dernière valeur définie pendant l'animation de sortie. Quand `valeur` repasse à
 * `undefined`, `affichee` conserve l'ancienne valeur `duree` millisecondes avec `sortie` à vrai,
 * le temps que le composant joue sa transition, puis devient `undefined` (démontage). Sans
 * animation (mouvement réduit), le démontage est immédiat.
 */
export function useSortieDifferee<T>(
  valeur: T | undefined,
  duree: number = DUREES.base,
): { affichee: T | undefined; sortie: boolean } {
  const [retenue, setRetenue] = useState<T | undefined>(valeur);
  // Synchronisation pendant le rendu : la nouvelle valeur est retenue sans attendre un effet.
  if (valeur !== undefined && valeur !== retenue) setRetenue(valeur);

  useEffect(() => {
    if (valeur !== undefined || retenue === undefined) return;
    if (mouvementReduit()) {
      setRetenue(undefined);
      return;
    }
    const minuteur = setTimeout(() => setRetenue(undefined), duree);
    return () => clearTimeout(minuteur);
  }, [valeur, retenue, duree]);

  const affichee = valeur ?? (mouvementReduit() ? undefined : retenue);
  return { affichee, sortie: valeur === undefined && affichee !== undefined };
}

/** Comportement de défilement respectant la préférence de mouvement. */
export function defilementDoux(): ScrollBehavior {
  return mouvementReduit() ? 'auto' : 'smooth';
}

/**
 * Présence booléenne : garde l'élément monté `duree` ms après la fermeture, le temps de sa sortie.
 * `propsSortie` est à poser sur la racine : l'élément sortant devient inerte et muet.
 */
export function usePresence(ouvert: boolean, duree: number = DUREES.rapide) {
  const { affichee, sortie } = useSortieDifferee(ouvert ? true : undefined, duree);
  return {
    monte: affichee !== undefined,
    propsSortie: {
      inert: sortie || undefined,
      'aria-hidden': sortie || undefined,
      'data-etat': sortie ? 'ferme' : 'ouvert',
    } as const,
  };
}

/**
 * Applique `maj` dans une View Transition quand le navigateur sait faire et que le mouvement est
 * permis ; sinon l'applique tout de suite. flushSync force le rendu avant la capture de la nouvelle vue.
 */
export function transitionVue(maj: () => void): void {
  if (typeof document.startViewTransition !== 'function' || mouvementReduit()) {
    maj();
    return;
  }
  document.startViewTransition(() => flushSync(maj));
}
